import { Injectable } from '@angular/core'
import { CountDraftLine, CountExtra, Handoff, OFFLINE_TOAST, ReportType, ToolCount, ToolInventory, ToolReport } from 'src/app/domain/tools'
import { ApiError } from 'src/app/utils/api-error'
import { ApiService } from './api.service'
import { LoggerService } from './logger.service'
import { ConnectionStatusEnum, NetworkService } from './network.service'
import { UiService } from './ui.service'

/** A kit line sends `found_tool_ids` (the tools checked off) and the API sets its `counted`. */
export interface CountWrite { readonly action: 'save' | 'submit'; readonly lines: readonly CountDraftLine[]; readonly extras: readonly CountExtra[] }

/** A return handed to a receiver (`to_user_id`, one of the inventory's receivers) or, left out, to the shop to confirm on the web. */
export interface ReturnWrite { readonly tool_id: number; readonly esign_name: string; readonly to_user_id?: number }
/** The whole kit on one signature; a tool marked missing needs the note to say where it is. */
export interface KitReturnWrite { readonly kit_id: number; readonly esign_name: string; readonly to_user_id?: number; readonly missing_tool_ids: readonly number[]; readonly note: string }

/** The API said no to the request itself (a rule failed, or the row is not the caller's), with its reason. */
const ruleMessage = (e: unknown): string | null => {
  if (!(e instanceof ApiError) || e.status == null || e.status < 400 || e.status >= 500) return null
  const data = e.responseData
  return typeof data?.message === 'string' ? data.message : typeof data?.error?.message === 'string' ? data.error.message : null
}

/** One method per /tool/ endpoint. A write returns the row the API sent back, or null after a toast. */
@Injectable({ providedIn: 'root' })
export class ToolService {
  constructor(private api: ApiService, private ui: UiService, private network: NetworkService) {}

  /** Silent on failure: this runs inside every refresh, offline included. */
  async getMine(): Promise<ToolInventory | null> {
    try {
      const ret = await this.api.apiReturnsAny('get', 'tool/inventory/mine')
      return ret && Array.isArray(ret.tools) ? (ret as ToolInventory) : null
    } catch (error) {
      LoggerService.log(`${error} tool/inventory/mine`)
      return null
    }
  }

  saveCount(id: number, body: CountWrite): Promise<ToolCount | null> {
    return this.send('put', `tool/count/${id}`, body)
  }

  transfer(body: { readonly tool_id: number; readonly to_user_id: number; readonly note: string }): Promise<Handoff | null> {
    return this.send('post', 'tool/handoff/transfer', body)
  }

  returnTool(body: ReturnWrite): Promise<Handoff | null> {
    return this.send('post', 'tool/handoff/return', body)
  }

  returnKit(body: KitReturnWrite): Promise<Handoff[] | null> {
    return this.send('post', 'tool/handoff/return', body)
  }

  accept(body: { readonly ids: readonly number[]; readonly esign_name: string }): Promise<Handoff[] | null> {
    return this.send('put', 'tool/handoff/accept', body)
  }

  decline(id: number, reply: string): Promise<Handoff | null> {
    return this.send('put', `tool/handoff/${id}/decline`, { reply })
  }

  cancel(id: number): Promise<Handoff | null> {
    return this.send('put', `tool/handoff/${id}/cancel`, {})
  }

  report(body: { readonly tool_id: number; readonly type: ReportType; readonly where: string; readonly what: string; readonly usable: boolean }): Promise<ToolReport | null> {
    return this.send('post', 'tool/report', body)
  }

  private async send<T>(method: 'post' | 'put', url: string, body: object): Promise<T | null> {
    if (this.network.currentStatus !== ConnectionStatusEnum.Online) {
      await this.ui.showToast(OFFLINE_TOAST)
      return null
    }
    try {
      const ret = await this.api.apiReturnsAny(method, url, body, 'application/json')
      return (ret ?? null) as T | null
    } catch (error) {
      LoggerService.log(`${error} ${method} ${url}`)
      await this.ui.showToast(ruleMessage(error) ?? OFFLINE_TOAST)
      return null
    }
  }
}
